import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_PLANS, TEAM_SIZES, formatUsd } from "../Pricing/planCatalog";
import { DOCS_URL as HELP_GUIDE_URL } from "../../support";
import {
  ActionList,
  Button,
  ButtonGroup,
  Frame,
  Icon,
  Popover,
  Text,
  TextField,
  Toast,
} from "@shopify/polaris";
import {
  BookOpenIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  ExternalIcon,
  MagicIcon,
  MenuHorizontalIcon,
  PersonAddIcon,
  TargetIcon,
  TeamIcon,
  XIcon,
} from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import useTrialOffer from "../../utils/trialOffer";
import useAxiosClient from "../axiosClient";
import { buildPortalUrl } from "../../utils/portal";
import portalImage from "../../images/onboarding/portal.webp";
import "../../i18n";

/**
 * Welcome screen for a new account.
 *
 * Who sees it is decided on the server (see the OnboardingState model): an
 * account that existed before this guide shipped carries a finished row and
 * never lands here. Everything this component writes goes back to that row, so
 * progress survives a reload and follows the user between browsers.
 *
 * Four steps, not the Shopify app's six. The backup-email step needs a users
 * column this app does not have, and the sales-target step has no page to point
 * at.
 */

const BOOK_A_CALL_URL = "https://calendly.com/talk-to-specialist/30min";
// Kept in step with OnboardingState::STEPS on the server. The team-size step
// is id 5: 3 and 4 are retired ids that older rows may still carry. 6 is the
// optional backup-email step - "skip" completes it, so it never blocks.
// 7, not 3 or 4 - those are retired ids that older rows may still carry.
// MUST match OnboardingState::STEPS on the server, which validates the id
// on save and decides when the whole guide is complete.
// Order here is DISPLAY order - the steps array below is rendered in sequence
// and numbered by index. Ids are persisted and 3/4 are retired, so 8 is the next
// free one. Must stay in sync with OnboardingState::STEPS, which validates
// incoming ids and decides when the guide is finished.
const STEP_IDS = [1, 2, 5, 8];
const PAY_FREQUENCY_STEP = 8;

/**
 * Exactly the three values staff.payrollType accepts, checked against the
 * validator at StaffManagementController:19229. Offering a fourth option here
 * would record a pay type nothing else in the app can honour.
 */
const PAY_FREQUENCIES = [
  { value: "Hourly", labelKey: "onboarding_payfreq_hourly" },
  { value: "Weekly", labelKey: "onboarding_payfreq_weekly" },
  { value: "Monthly", labelKey: "onboarding_payfreq_monthly" },
];

// Deliberately loose: the server validates properly; this only stops an
// obvious typo making a round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOTAL_STEPS = STEP_IDS.length;

// Green reads as "chosen" wherever it appears. Buttons stay on Polaris' own
// near-black primary, so the accent never competes with the real action.
const SELECTED_ACCENT = "#008060";

/** Filled tick for a finished step, dashed ring for one still to do. */
function StepMarker({ complete }) {
  if (complete) {
    return (
      <span
        style={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "#303030",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M1.5 6.2 4.4 9 10.5 2.9"
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  return (
    <span
      style={{
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        border: "2px dashed #b5b5b5",
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

/**
 * Palette for the step 2 drawings.
 *
 * Step 1 shows real product screenshots; step 2's options are a distinction in
 * process rather than in product, so they stay drawn. One palette and one
 * 2.4px outline weight keep the pair reading as a set.
 */
const ART = {
  line: "#3f4b52",
  surface: "#ffffff",
  muted: "#cdd2d6",
  screen: "#dfeafa",
  navy: "#2c4f76",
  green: "#4a8f63",
  shadow: "#e4e7ea",
};

/** Filling in the staff form by hand. */
function ManualStaffIllustration() {
  return (
    <svg
      viewBox="0 0 180 150"
      style={{ width: "100%", height: "auto", display: "block" }}
      aria-hidden="true"
    >
      <ellipse cx="88" cy="136" rx="62" ry="6" fill={ART.shadow} />

      {/* Staff record being filled in */}
      <rect x="28" y="20" width="112" height="102" rx="10" fill={ART.surface} stroke={ART.line} strokeWidth="2.4" />
      <circle cx="55" cy="47" r="14" fill={ART.screen} stroke={ART.line} strokeWidth="2.2" />
      <circle cx="55" cy="43" r="5" fill={ART.navy} />
      <path d="M47 57a8 8 0 0 1 16 0z" fill={ART.navy} />
      <rect x="77" y="38" width="46" height="6" rx="3" fill="#cdd2d6" />
      <rect x="77" y="50" width="32" height="6" rx="3" fill={ART.muted} />
      <rect x="42" y="74" width="84" height="12" rx="6" fill="#eef1f4" stroke={ART.muted} strokeWidth="2" />
      <rect x="42" y="94" width="84" height="12" rx="6" fill="#eef1f4" stroke={ART.muted} strokeWidth="2" />

      {/* Add badge */}
      <circle cx="136" cy="32" r="15" fill={ART.green} stroke={ART.line} strokeWidth="2.4" />
      <path
        d="M136 25v14M129 32h14"
        stroke={ART.surface}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** A ready-made staff member, PIN and all. */
function TestStaffIllustration() {
  return (
    <svg
      viewBox="0 0 180 150"
      style={{ width: "100%", height: "auto", display: "block" }}
      aria-hidden="true"
    >
      <ellipse cx="86" cy="134" rx="60" ry="6" fill={ART.shadow} />

      {/* Staff card that arrives already filled in */}
      <rect x="28" y="26" width="112" height="94" rx="10" fill={ART.surface} stroke={ART.line} strokeWidth="2.4" />
      <circle cx="55" cy="52" r="13" fill={ART.screen} stroke={ART.line} strokeWidth="2.2" />
      <circle cx="55" cy="48" r="4.5" fill={ART.navy} />
      <path d="M47.5 61a7.5 7.5 0 0 1 15 0z" fill={ART.navy} />
      <rect x="76" y="44" width="44" height="6" rx="3" fill="#cdd2d6" />
      <rect x="76" y="56" width="30" height="6" rx="3" fill={ART.muted} />

      {/* PIN */}
      <rect x="42" y="80" width="18" height="22" rx="4" fill={ART.screen} stroke={ART.line} strokeWidth="2" />
      <rect x="64" y="80" width="18" height="22" rx="4" fill={ART.screen} stroke={ART.line} strokeWidth="2" />
      <rect x="86" y="80" width="18" height="22" rx="4" fill={ART.screen} stroke={ART.line} strokeWidth="2" />
      <rect x="108" y="80" width="18" height="22" rx="4" fill={ART.screen} stroke={ART.line} strokeWidth="2" />
      <circle cx="51" cy="91" r="3.5" fill={ART.navy} />
      <circle cx="73" cy="91" r="3.5" fill={ART.navy} />
      <circle cx="95" cy="91" r="3.5" fill={ART.navy} />
      <circle cx="117" cy="91" r="3.5" fill={ART.navy} />

      {/* Done-for-you sparkle */}
      <path
        d="M148 22l3.5 7.5 7.5 3.5-7.5 3.5-3.5 7.5-3.5-7.5-7.5-3.5 7.5-3.5z"
        fill={ART.green}
        stroke={ART.line}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M160 52l2.2 4.8 4.8 2.2-4.8 2.2-2.2 4.8-2.2-4.8-4.8-2.2 4.8-2.2z"
        fill={ART.green}
      />
    </svg>
  );
}

/** A picture-led option: illustration, label, and a one-line clarifier. */
/**
 * Trailing arrow for an action that takes you somewhere.
 *
 * Polaris' Button exposes a leading `icon` only, so this rides in the button's
 * children. It paints with currentColor so it tracks the button's own text
 * colour through hover, focus and disabled without any extra plumbing.
 */
function ButtonArrow() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path
        d="M2.5 8h11M9 3.5 13.5 8 9 12.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Wraps button text so a trailing glyph sits on the text baseline. */
function ButtonLabelWithArrow({ children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      {children}
      <ButtonArrow />
    </span>
  );
}

/** Empty ring when unpicked, filled tick when picked. */
function SelectionBadge({ selected }) {
  return (
    <span
      style={{
        position: "absolute",
        top: "12px",
        right: "12px",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: selected ? SELECTED_ACCENT : "#ffffff",
        border: selected ? `2px solid ${SELECTED_ACCENT}` : "1.5px solid #c9cccf",
        transition: "background 0.15s ease-in-out, border-color 0.15s ease-in-out",
      }}
    >
      {selected && (
        <svg width="13" height="13" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M1.5 6.2 4.4 9 10.5 2.9"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

/**
 * A picture-led option.
 *
 * `wide` tiles share the row and carry a product screenshot; the narrow default
 * holds a drawn illustration. Both show the same selection badge so the two
 * steps read as one pattern.
 */
function ChoiceTile({ selected, onSelect, label, hint, wide = false, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        // Capped rather than stretched: the fine detail in these screenshots
        // is unreadable at any size here, so extra height buys nothing and
        // pushes step 2 off the screen.
        ...(wide
          ? { flex: "1 1 300px", minWidth: "260px", maxWidth: "430px" }
          : { width: "200px" }),
        padding: 0,
        border: 0,
        background: "transparent",
        font: "inherit",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: selected ? "#f1faf4" : "#f6f6f7",
          // A ring rather than a fatter border, so picking an option never
          // nudges the layout by a pixel.
          border: "1px solid #e3e3e3",
          boxShadow: selected ? `0 0 0 2px ${SELECTED_ACCENT}` : "none",
          borderRadius: "12px",
          padding: wide ? "18px" : "14px",
          transition: "box-shadow 0.15s ease-in-out, background 0.15s ease-in-out",
        }}
      >
        <SelectionBadge selected={selected} />
        {children}
      </span>

      <span style={{ display: "block", marginTop: "12px" }}>
        <Text variant={wide ? "headingMd" : "bodyMd"} fontWeight={selected ? "bold" : "semibold"}>
          {label}
        </Text>
      </span>

      {hint && (
        <span style={{ display: "block", marginTop: "2px" }}>
          <Text variant="bodyMd" tone="subdued">
            {hint}
          </Text>
        </span>
      )}
    </button>
  );
}

/* Glyphs for the plan recommendation - the same shapes the pricing cards use. */
const RecommendStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const RecommendCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={SELECTED_ACCENT} strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Compact answer chip for a one-of-many question; the accent ring means "chosen". */
function OptionPill({ selected, onSelect, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        font: "inherit",
        fontSize: "13px",
        fontWeight: 600,
        padding: "9px 16px",
        borderRadius: "999px",
        cursor: "pointer",
        background: selected ? "#f1faf5" : "#ffffff",
        color: selected ? "#0c5132" : "#303030",
        // Ring, not a thicker border, so choosing never shifts the row.
        border: "1px solid #e3e3e3",
        boxShadow: selected ? `0 0 0 2px ${SELECTED_ACCENT}` : "none",
        transition: "box-shadow 120ms ease, background 120ms ease",
      }}
    >
      {children}
    </button>
  );
}

function Onboarding({ onFinish }) {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trialDays } = useTrialOffer(axiosClient);

  const [isLoading, setIsLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [choices, setChoices] = useState({});
  const [shopName, setShopName] = useState("");
  const [staffCount, setStaffCount] = useState(0);
  const [hasClockedIn, setHasClockedIn] = useState(false);
  const [planId, setPlanId] = useState(null);
  const [hasPlan, setHasPlan] = useState(false);
  const [isFreemium, setIsFreemium] = useState(0);

  // Step 4 (optional): a second address for urgent account matters.
  const [shopEmail, setShopEmail] = useState("");
  const [emergencyEmail, setEmergencyEmail] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");

  const [openStep, setOpenStep] = useState(null);
  const [guideExpanded, setGuideExpanded] = useState(true);
  const [showCallCard, setShowCallCard] = useState(true);
  const [showPlanBanner, setShowPlanBanner] = useState(true);
  const [menuActive, setMenuActive] = useState(false);
  const [toastContent, setToastContent] = useState("");

  // The throwaway staff member step 2 can create, and its PIN.
  const [testStaff, setTestStaff] = useState(null);
  const [creatingTestStaff, setCreatingTestStaff] = useState(false);
  const [testStaffError, setTestStaffError] = useState("");
  const [atStaffLimit, setAtStaffLimit] = useState(false);

  // The staff-count auto-tick must fire once, not on every re-render.
  const autoCompletedRef = useRef(false);
  const defaultedStep1Ref = useRef(false);

  const isComplete = useCallback(
    (step) => completedSteps.includes(step),
    [completedSteps]
  );

  const portalUrl = useMemo(() => buildPortalUrl(shopName), [shopName]);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const response = await axiosClient.get("/onboarding-status");
        if (cancelled || !response.data) return;

        const steps = Array.isArray(response.data.steps) ? response.data.steps : [];
        setCompletedSteps(steps);
        setChoices(response.data.choices || {});
        setShopName(response.data.shop || "");
        setStaffCount(Number(response.data.staffCount) || 0);
        setHasClockedIn(Boolean(response.data.hasClockedIn));
        setPlanId(response.data.planId ?? null);
        setHasPlan(Boolean(response.data.hasPlan));
        setIsFreemium(Number(response.data.isFreemium) || 0);
        setShopEmail(response.data.shopEmail || "");
        setEmergencyEmail(response.data.emergencyEmail || "");
        setEmailDraft(response.data.emergencyEmail || "");

        // Open the first step still outstanding, the way the guide reads.
        const firstOpen = STEP_IDS.find((step) => !steps.includes(step));
        setOpenStep(firstOpen || null);
      } catch (error) {
        // Leave the guide on its defaults rather than blocking the welcome page.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  /** Persist one step, and keep the local copy in step with what came back. */
  const saveStep = useCallback(
    async (step, { complete = true, choice } = {}) => {
      const payload = { step, complete };
      if (choice !== undefined) payload.choice = choice;

      setCompletedSteps((prev) => {
        const next = prev.filter((id) => id !== step);
        if (complete) next.push(step);
        return next.sort((a, b) => a - b);
      });

      if (choice !== undefined) {
        setChoices((prev) => ({ ...prev, [String(step)]: choice }));
      }

      try {
        const response = await axiosClient.post("/onboarding-step", payload);
        if (response.data && Array.isArray(response.data.steps)) {
          setCompletedSteps(response.data.steps);
          setChoices(response.data.choices || {});
        }
      } catch (error) {
        // The optimistic state above still reads correctly for this session.
      }
    },
    [axiosClient]
  );

  // Step 1 arrives already answered.
  //
  // This build offers one way to clock in, so asking the merchant to pick it is
  // asking them to confirm the only thing it could be. The choice is still
  // recorded rather than assumed at read time, because the rest of the guide
  // and the server both key off choices["1"] - leaving it unset would show the
  // step as outstanding forever while nothing was outstanding.
  useEffect(() => {
    if (isLoading || defaultedStep1Ref.current) return;
    defaultedStep1Ref.current = true;

    if (!choices["1"]) {
      saveStep(1, { complete: true, choice: "portal" });
    }
  }, [isLoading, choices, saveStep]);

  // A shop that already has staff has plainly done step 2 - do not make them
  // tick it off by hand.
  useEffect(() => {
    if (isLoading || autoCompletedRef.current) return;
    autoCompletedRef.current = true;

    if (hasClockedIn && !completedSteps.includes(2)) {
      saveStep(2, { complete: true });
    }
  }, [isLoading, hasClockedIn, completedSteps, saveStep]);

  const finishGuide = useCallback(
    async (dismissed) => {
      try {
        await axiosClient.post("/onboarding-finish", { dismissed });
      } catch (error) {
        // Fall through: the merchant asked to leave, so let them leave.
      }

      // The dashboard carries its own older setup card covering the same
      // ground. Someone who just worked through this guide should not be
      // handed it again the moment they land there.
      try {
        localStorage.setItem("zonstaffsetupGuideDismissed", "true");
      } catch (error) {
        // Private-mode storage failures are not worth blocking navigation for.
      }

      if (typeof onFinish === "function") {
        onFinish();
      } else {
        navigate("/");
      }
    },
    [axiosClient, navigate, onFinish]
  );

  const saveEmergencyEmail = useCallback(async () => {
    const email = emailDraft.trim().toLowerCase();

    if (!EMAIL_PATTERN.test(email)) {
      setEmailError(t("onboarding_step4_invalid"));
      return;
    }

    setEmailError("");
    setSavingEmail(true);

    try {
      const response = await axiosClient.post("/onboarding-emergency-email", { email });

      if (response.data && response.data.success) {
        setEmergencyEmail(response.data.emergencyEmail || email);
        setEditingEmail(false);

        if (Array.isArray(response.data.steps)) {
          setCompletedSteps(response.data.steps);
          setChoices(response.data.choices || {});
        }

        setToastContent(t("onboarding_step4_saved_toast"));
      } else {
        setEmailError(t("onboarding_step4_failed"));
      }
    } catch (error) {
      setEmailError(t("onboarding_step4_failed"));
    } finally {
      setSavingEmail(false);
    }
  }, [axiosClient, emailDraft, t]);

  const copyPortalLink = useCallback(async () => {
    if (!portalUrl) return;

    try {
      await navigator.clipboard.writeText(portalUrl);
    } catch (error) {
      // The clipboard API is blocked in some embedded contexts - fall back.
      const field = document.createElement("textarea");
      field.value = portalUrl;
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand("copy");
      } catch (copyError) {
        // Nothing else to try; the link is on screen to copy by hand.
      }
      document.body.removeChild(field);
    }

    setToastContent(t("onboarding_portal_link_copied"));
  }, [portalUrl, t, isComplete, saveStep]);

  const completedCount = completedSteps.length;
  const allDone = completedCount >= TOTAL_STEPS;
  const progress = Math.min(100, Math.round((completedCount / TOTAL_STEPS) * 100));

  // Only the free plan carries a staff cap worth warning about up front.
  const showFreePlanNotice = showPlanBanner && !planId && isFreemium === 1;

  // Step 3: what the merchant told us about their team, and the plan whose
  // cap covers it. Only a shop on the free plan gets a suggestion - a paying
  // shop already chose, and second-guessing that on a welcome screen is noise.
  const teamSize = TEAM_SIZES.find((size) => size.value === choices["5"]) || null;
  const payFrequency =
    PAY_FREQUENCIES.find(
      (option) => option.value === choices[String(PAY_FREQUENCY_STEP)]
    ) || null;
  // Free means no live Stripe subscription. plan_id alone is not the test here:
  // it is nulled on downgrade, and Pricing.jsx uses stripe_subscription_id for
  // exactly this question, so the two must agree.
  const onFreePlan = !hasPlan;
  const suggestedPlan = teamSize && teamSize.plan && onFreePlan ? BASE_PLANS[teamSize.plan] : null;
  // 0 unless this account would really be granted a trial on the plan being
  // suggested - the same rule the pricing cards and the seat wall use.
  const suggestedTrialDays = suggestedPlan ? trialDays(suggestedPlan.key) : 0;
  const suggestedHighlights = suggestedPlan
    ? [
      t("onboarding_team_plan_staff_cap", { staff: suggestedPlan.staff }),
      ...suggestedPlan.highlightKeys.map((key) => t(key)),
    ]
    : [];

  const toggleStep = (step) => setOpenStep((prev) => (prev === step ? null : step));

  const openPortal = () => {
    if (portalUrl) window.open(portalUrl, "_blank");
  };

  /** Best-effort copy that works even where the async clipboard is blocked. */
  const copyText = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch (error) {
      // Embedded frames can refuse the clipboard API - fall back below.
    }

    const field = document.createElement("textarea");
    field.value = value;
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand("copy");
    } catch (copyError) {
      // Nothing else to try; the value is on screen to copy by hand.
    }
    document.body.removeChild(field);
  };

  const copyPin = async () => {
    if (!testStaff) return;
    await copyText(testStaff.pin);
    setToastContent(t("onboarding_pin_copied"));
  };

  /**
   * The endpoint is idempotent - a second click returns the staff member the
   * first one made rather than spending another slot on the merchant's plan.
   */
  const createTestStaff = async () => {
    setCreatingTestStaff(true);
    setTestStaffError("");

    try {
      const response = await axiosClient.post("/onboarding-test-staff");
      const data = response.data || {};

      if (data.success) {
        setTestStaff({ name: data.name, pin: data.pin });
        saveStep(2, { complete: true, choice: "test" });
      } else if (data.reason === "limit") {
        setAtStaffLimit(true);
        setTestStaffError(
          t("onboarding_test_staff_limit", {
            used: data.used ?? 0,
            allowance: data.allowance ?? 0,
          })
        );
      } else {
        setTestStaffError(t("onboarding_test_staff_failed"));
      }
    } catch (error) {
      setTestStaffError(t("onboarding_test_staff_failed"));
    } finally {
      setCreatingTestStaff(false);
    }
  };

  // The smart grid editor is where a tile is actually placed. The POS UI
  // extensions settings page only lists what is installed, which left the
  // merchant one hop short of the thing the button promises.
  const openPosTileEditor = () => {
    if (!shopName) return;
    window.open(
      `https://admin.shopify.com/store/${shopName}/apps/point-of-sale-channel/editor`,
      "_blank"
    );
  };

  // Each path's actions live in one place and are rendered both under the
  // step 1 choice and in the step that owns them - two copies would drift.
  const renderPortalActions = (extra = null) => (
    <>
      {portalUrl && (
        <div
          style={{
            marginTop: "12px",
            padding: "10px 12px",
            background: "#f7f7f7",
            border: "1px solid #e3e3e3",
            borderRadius: "8px",
            wordBreak: "break-all",
          }}
        >
          <Text variant="bodySm" as="span" tone="subdued">
            {portalUrl}
          </Text>
        </div>
      )}
      <div style={{ marginTop: "16px" }}>
        <ButtonGroup>
          <Button variant="primary" icon={ClipboardIcon} onClick={copyPortalLink} disabled={!portalUrl}>
            {t("onboarding_copy_portal_link")}
          </Button>
          <Button icon={ExternalIcon} onClick={openPortal} disabled={!portalUrl}>
            {t("onboarding_open_portal")}
          </Button>
          {extra}
        </ButtonGroup>
      </div>
    </>
  );

  const renderPosActions = (extra = null) => (
    <div style={{ marginTop: "16px" }}>
      <ButtonGroup>
        <Button variant="primary" disabled={!shopName} onClick={openPosTileEditor}>
          <ButtonLabelWithArrow>{t("onboarding_add_to_pos")}</ButtonLabelWithArrow>
        </Button>
        <Button icon={BookOpenIcon} onClick={() => window.open(HELP_GUIDE_URL, "_blank")}>
          {t("help_guide")}
        </Button>
        {extra}
      </ButtonGroup>
    </div>
  );

  const steps = [
    {
      id: 1,
      title: t("onboarding_step1_title"),
      body: (
        <div>
          <Text variant="bodyMd" as="p">
            {t("onboarding_step1_prompt")}
          </Text>

          {/* Side by side, not stacked.
              This build offers one way to clock in, so there is nothing to
              choose - and a single 430px tile on its own row left most of the
              width empty and pushed the link the merchant actually has to send
              below the fold. The tile now says what the portal is and the link
              sits beside it. Stacks under 900px; see .onb-step1.

              The POS branch that used to follow is gone with it: it could only
              fire on choices["1"] === "pos", and this build has no POS tile to
              set that. */}
          <div className="onb-step1">
            <ChoiceTile
              wide
              selected={choices["1"] === "portal"}
              onSelect={() => saveStep(1, { complete: true, choice: "portal" })}
              label={t("onboarding_step1_tile_portal")}
              hint={t("onboarding_step1_tile_portal_hint")}
            >
              <img
                src={portalImage}
                alt=""
                loading="lazy"
                style={{ width: "100%", height: "auto", display: "block", borderRadius: "6px" }}
              />
            </ChoiceTile>

            <div>
              <Text variant="bodyMd" as="p">
                {t("onboarding_portal_hint")}
              </Text>
              {renderPortalActions()}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: t("onboarding_step2_title"),
      body: (
        <div style={{ maxWidth: "640px" }}>
          <Text variant="bodyMd" as="p">
            {t("onboarding_step2_prompt")}
          </Text>

          <div
            style={{
              display: "flex",
              gap: "22px",
              marginTop: "14px",
              flexWrap: "wrap",
            }}
          >
            <ChoiceTile
              selected={choices["2"] === "manual"}
              onSelect={() => saveStep(2, { complete: isComplete(2), choice: "manual" })}
              label={t("onboarding_step2_tile_manual")}
              hint={t("onboarding_step2_tile_manual_hint")}
            >
              <ManualStaffIllustration />
            </ChoiceTile>

            <ChoiceTile
              selected={choices["2"] === "test"}
              onSelect={() => saveStep(2, { complete: isComplete(2), choice: "test" })}
              label={t("onboarding_step2_tile_test")}
              hint={t("onboarding_step2_tile_test_hint")}
            >
              <TestStaffIllustration />
            </ChoiceTile>
          </div>

          {staffCount > 0 && !hasClockedIn && (
            <div style={{ marginTop: "14px" }}>
              <Text variant="bodySm" as="p" tone="subdued">
                {t("onboarding_step2_awaiting_clockin", { count: staffCount })}
              </Text>
            </div>
          )}

          {/* Step 2 is where a free-plan merchant meets the one-seat limit, so
              it is the honest moment to price the whole team. Priced from
              BASE_PLANS so it cannot drift from the pricing cards. Secondary
              button, quiet surface: it must not outrank the step's own task. */}
          {onFreePlan && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginTop: "16px",
                padding: "12px 14px",
                borderRadius: "10px",
                background: "#f7f7f7",
                border: "1px solid #e3e3e3",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#e3e3e3",
                }}
              >
                {/* .Polaris-Icon carries margin:auto, so it is pinned inside
                    the disc rather than sized directly. */}
                <span style={{ display: "flex", width: 16, height: 16 }}>
                  <Icon source={TeamIcon} tone="base" />
                </span>
              </span>

              <div style={{ flex: 1, minWidth: "220px" }}>
                <Text variant="bodySm" as="p" tone="subdued">
                  {t("onboarding_step2_plan_note", {
                    price: formatUsd(BASE_PLANS.standard.price),
                    staff: BASE_PLANS.standard.staff,
                  })}
                </Text>
              </div>

              <div style={{ flexShrink: 0 }}>
                <Button onClick={() => navigate("/Pricing")}>
                  {t("onboarding_free_plan_cta")}
                </Button>
              </div>
            </div>
          )}

          {choices["2"] === "manual" && (
            <div style={{ marginTop: "22px", paddingTop: "18px", borderTop: "1px solid #e3e3e3" }}>
              <ButtonGroup>
                <Button
                  variant="primary"
                  icon={PersonAddIcon}
                  onClick={() => navigate("/StaffManagement/AddNonShopifyStaffPage")}
                >
                  {t("onboarding_step2_action_manual")}
                </Button>
                {!isComplete(2) && (
                  <Button variant="tertiary" onClick={() => saveStep(2, { complete: true })}>
                    {t("mark_as_done")}
                  </Button>
                )}
              </ButtonGroup>
            </div>
          )}

          {choices["2"] === "test" && (
            <div style={{ marginTop: "22px", paddingTop: "18px", borderTop: "1px solid #e3e3e3" }}>
              {!testStaff && (
                <ButtonGroup>
                  <Button variant="primary" icon={MagicIcon} loading={creatingTestStaff} onClick={createTestStaff}>
                    {t("onboarding_step2_action_test")}
                  </Button>
                </ButtonGroup>
              )}

              {testStaffError && (
                <div style={{ marginTop: "12px" }}>
                  <Text variant="bodyMd" as="p" tone="critical">
                    {testStaffError}
                  </Text>

                  {/* A dead end is not an answer: hitting the cap offers the
                      two things that actually resolve it. */}
                  {atStaffLimit && (
                    <div style={{ marginTop: "12px" }}>
                      <ButtonGroup>
                        <Button variant="primary" onClick={() => navigate("/Pricing")}>
                          {t("onboarding_view_plans")}
                        </Button>
                        <Button onClick={() => navigate("/StaffManagement")}>
                          {t("onboarding_manage_staff")}
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}
                </div>
              )}

              {testStaff && (
                <div
                  style={{
                    padding: "16px",
                    background: "#f6f6f7",
                    border: "1px solid #e3e3e3",
                    borderRadius: "10px",
                  }}
                >
                  <Text variant="headingSm" as="h4">
                    {t("onboarding_test_staff_created", { name: testStaff.name })}
                  </Text>

                  <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <Text variant="bodyMd" as="span" tone="subdued">
                      {t("onboarding_test_staff_pin_label")}
                    </Text>
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        letterSpacing: "4px",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        color: "#303030",
                      }}
                    >
                      {testStaff.pin}
                    </span>
                  </div>

                  <div style={{ marginTop: "10px" }}>
                    <Text variant="bodyMd" as="p">
                      {t("onboarding_test_staff_hint")}
                    </Text>
                  </div>

                  <div style={{ marginTop: "14px" }}>
                    <ButtonGroup>
                      <Button variant="primary" icon={ExternalIcon} onClick={openPortal} disabled={!portalUrl}>
                        {t("onboarding_open_portal")}
                      </Button>
                      <Button icon={ClipboardIcon} onClick={copyPin}>{t("onboarding_copy_pin")}</Button>
                    </ButtonGroup>
                  </div>

                  <div style={{ marginTop: "12px" }}>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {t("onboarding_test_staff_note")}
                    </Text>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 5,
      title: t("onboarding_step3_title"),
      body: (
        <div style={{ maxWidth: "640px" }}>
          <Text variant="bodyMd" as="p">
            {t("onboarding_step3_prompt")}
          </Text>

          {/* Answering IS the task, so a pick completes the step and the
              suggestion appears underneath - no separate "mark as done". */}
          <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
            {TEAM_SIZES.map((size) => (
              <OptionPill
                key={size.value}
                selected={choices["5"] === size.value}
                onSelect={() => saveStep(5, { complete: true, choice: size.value })}
              >
                {t(size.labelKey)}
              </OptionPill>
            ))}
          </div>

          {teamSize && (
            <div style={{ marginTop: "22px", paddingTop: "18px", borderTop: "1px solid #e3e3e3" }}>
              {suggestedPlan ? (
                <div
                  style={{
                    maxWidth: "560px",
                    background: "#ffffff",
                    border: "1px solid #e3e3e3",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 1px 1px rgba(26, 26, 26, 0.05), 0 2px 6px rgba(26, 26, 26, 0.09)",
                  }}
                >
                  {/* Same strip the pricing cards wear, so this reads as "one of those". */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "9px 16px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: SELECTED_ACCENT,
                      background: "rgba(0, 128, 96, 0.07)",
                    }}
                  >
                    <RecommendStar />
                    {t("onboarding_team_recommended_badge")}
                  </div>

                  <div
                    style={{
                      padding: "16px 18px 18px",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <Text variant="headingMd" as="h4">
                        {t(suggestedPlan.nameKey)}
                      </Text>

                      <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginTop: "6px" }}>
                        <span
                          style={{
                            fontSize: "26px",
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: "-0.4px",
                            color: "#303030",
                          }}
                        >
                          {formatUsd(suggestedPlan.price)}
                        </span>
                        <Text variant="bodySm" as="span" tone="subdued">
                          {t("per_month")}
                        </Text>
                      </div>

                      <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
                        {suggestedHighlights.map((line) => (
                          <li
                            key={line}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              fontSize: "13px",
                              color: "#303030",
                              marginBottom: "6px",
                            }}
                          >
                            <RecommendCheck />
                            {line}
                          </li>
                        ))}
                      </ul>

                      {teamSize.value === "100+" && (
                        <div style={{ marginTop: "4px" }}>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {t("onboarding_team_recommend_enterprise_plus")}
                          </Text>
                        </div>
                      )}
                    </div>

                    {/* The action sits under what it buys, not beside it: the
                        points are the argument, so the buttons come after them. */}
                    <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #e3e3e3" }}>
                      {/* Straight to the pricing page rather than a checkout:
                          billing here is Stripe, driven from that page.

                          The trial IS real now - StripeController puts
                          trial_period_days on the session - so this may offer
                          it, but only on the same terms the checkout honours.
                          suggestedTrialDays is 0 whenever the plan grants no
                          trial or this account has already had one, and the
                          button falls back to its old wording. */}
                      <ButtonGroup>
                        <Button variant="primary" onClick={() => navigate("/Pricing")}>
                          {suggestedTrialDays > 0
                            ? t("seatwall_start_trial", { days: suggestedTrialDays })
                            : t("onboarding_team_subscribe")}
                        </Button>
                        <Button onClick={() => navigate("/Pricing")}>
                          {t("onboarding_view_plans")}
                        </Button>
                      </ButtonGroup>

                      <div style={{ marginTop: "8px" }}>
                        <Text variant="bodySm" as="p" tone="subdued">
                          {t("onboarding_team_billing_note")}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Text variant="bodyMd" as="p" tone="subdued">
                  {!teamSize.plan && onFreePlan
                    ? t("onboarding_team_fits_free")
                    : t("onboarding_team_noted")}
                </Text>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      id: PAY_FREQUENCY_STEP,
      title: t("onboarding_payfreq_title"),
      body: (
        <div style={{ maxWidth: "640px" }}>
          <Text variant="bodyMd" as="p">
            {t("onboarding_payfreq_prompt")}
          </Text>

          <div style={{ marginTop: "6px" }}>
            <Text variant="bodySm" as="p" tone="subdued">
              {t("onboarding_payfreq_hint")}
            </Text>
          </div>

          {/* Answering IS the task, as on the team-size step - a pick completes
              it, so there is no separate "mark as done". */}
          <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
            {PAY_FREQUENCIES.map((option) => (
              <OptionPill
                key={option.value}
                selected={choices[String(PAY_FREQUENCY_STEP)] === option.value}
                onSelect={() =>
                  saveStep(PAY_FREQUENCY_STEP, { complete: true, choice: option.value })
                }
              >
                {t(option.labelKey)}
              </OptionPill>
            ))}
          </div>

          {payFrequency && (
            <div style={{ marginTop: "12px" }}>
              <Text variant="bodySm" as="p" tone="subdued">
                {t("onboarding_payfreq_saved", { label: t(payFrequency.labelKey) })}
              </Text>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    // No bottom padding of its own: AppFooter renders straight after this with
    // mt-10 already on it, and adding more here just opened a gap.
    <div style={{ paddingTop: "8px" }}>
      {toastContent !== "" && (
        <Frame>
          <Toast content={toastContent} onDismiss={() => setToastContent("")} />
        </Frame>
      )}

      {showFreePlanNotice && (
        // Polaris 13 paints banners on a white surface with a tinted icon chip,
        // which reads flat against the grey page, so this strip is drawn here.
        // Deliberately informational rather than a warning: being on the free
        // plan is not a fault - it is assigned on install - and an alert
        // triangle on a merchant's first screen contradicts the copy beside it.
        <div
          style={{
            maxWidth: "700px",
            margin: "0 auto 28px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            background: "#fdf5e9",
            border: "1px solid #e6d3b3",
            borderRadius: "10px",
            padding: "14px 16px",
            boxShadow: "0 1px 3px rgba(26, 26, 26, 0.08)",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 9,
              background: "#f5e6c8",
            }}
          >
            <Icon source={TeamIcon} tone="base" />
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text variant="bodyMd" as="p" fontWeight="semibold">
              {t("onboarding_free_plan_title")}
            </Text>
            <div style={{ marginTop: "2px" }}>
              <Text variant="bodySm" as="p" tone="subdued">
                {t("onboarding_free_plan_body")}
              </Text>
            </div>
          </div>

          {/* A verb, not "Click here": the link has to say what it does, both to
              earn the click and because a screen reader reads links out of context. */}
          <div style={{ flexShrink: 0 }}>
            <Button variant="primary" onClick={() => navigate("/Pricing")}>
              {t("onboarding_free_plan_cta")}
            </Button>
          </div>

          <button
            type="button"
            aria-label={t("onboarding_dismiss")}
            onClick={() => setShowPlanBanner(false)}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              lineHeight: 0,
              padding: "2px",
              flexShrink: 0,
            }}
          >
            <Icon source={XIcon} tone="base" />
          </button>
        </div>
      )}

      {/* The app shell is <Page fullWidth>, so the guide sets its own column.
          998px was Polaris' standard page width, but the shell has already paid
          a 260px sidebar and a 28px gutter by the time this renders, so a second
          centred column inset the guide twice and left it floating in the middle
          of the content area - the only route in the app that does not fill it.
          1280 fills that area on a normal laptop and still caps the guide on a
          very wide monitor, where a setup checklist read edge to edge would be
          worse, not better. The reading blocks inside keep their own 640px. */}
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* No brand and no language switcher here.
            Unlike the welcome screen, this one renders INSIDE the app shell,
            which already draws the mark in the sidebar and the language control
            in the header - so both appeared twice on one screen. The welcome
            screen keeps its own because it renders with no shell at all. */}

        {showCallCard && (
          <div
            style={{
              position: "relative",
              background: "rgb(224 240 255)",
              border: "1px solid #b8d8f2",
              borderRadius: "12px",
              padding: "10px 56px",
              textAlign: "center",
              marginBottom: "26px",
            }}
          >
            <button
              type="button"
              aria-label={t("onboarding_dismiss")}
              onClick={() => setShowCallCard(false)}
              style={{
                position: "absolute",
                top: "14px",
                right: "16px",
                background: "transparent",
                border: 0,
                cursor: "pointer",
                lineHeight: 0,
                padding: "4px",
              }}
            >
              <Icon source={XIcon} tone="base" />
            </button>

            <Text variant="headingMd" as="h2">
              {t("onboarding_make_setup_easy")}
            </Text>
            <div style={{ marginTop: "6px" }}>
              <Text variant="bodyLg" as="p">
                {t("onboarding_book_a_call_subtitle")}
              </Text>
            </div>
            <div style={{ marginTop: "16px" }}>
              <Button variant="primary" icon={CalendarIcon} onClick={() => window.open(BOOK_A_CALL_URL, "_blank")}>
                {t("onboarding_book_a_call")}
              </Button>
            </div>
          </div>
        )}

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e3e3e3",
            borderRadius: "12px",
            overflow: "hidden",
            // Two layers: a tight contact shadow to seat the card on the grey
            // page, and a wider soft one for the lift. A single faint shadow
            // reads as flat at this card size.
            boxShadow:
              "0 1px 1px rgba(26, 26, 26, 0.05), 0 2px 6px rgba(26, 26, 26, 0.09)",
          }}
        >
          <div style={{ padding: "20px 24px", display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text variant="headingMd" as="h2">
                {t("onboarding_guide_title")}
              </Text>
              <div style={{ marginTop: "4px" }}>
                <Text variant="bodyMd" as="p" tone="subdued">
                  {t("onboarding_guide_subtitle")}
                </Text>
              </div>

              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Text variant="bodyMd" as="span" tone="subdued">
                  {t("onboarding_steps_completed", {
                    completed: completedCount,
                    total: TOTAL_STEPS,
                  })}
                </Text>
                <span
                  style={{
                    width: "130px",
                    height: "6px",
                    borderRadius: "999px",
                    background: "#e3e3e3",
                    overflow: "hidden",
                    display: "inline-block",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      width: `${progress}%`,
                      height: "100%",
                      borderRadius: "999px",
                      background: "#303030",
                      transition: "width 0.2s ease-in-out",
                    }}
                  />
                </span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
              <Popover
                active={menuActive}
                activator={
                  <Button
                    variant="tertiary"
                    icon={MenuHorizontalIcon}
                    accessibilityLabel={t("onboarding_more_actions")}
                    onClick={() => setMenuActive((active) => !active)}
                  />
                }
                onClose={() => setMenuActive(false)}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    {
                      content: t("onboarding_skip_setup"),
                      onAction: () => {
                        setMenuActive(false);
                        finishGuide(true);
                      },
                    },
                    {
                      content: t("help_guide"),
                      onAction: () => {
                        setMenuActive(false);
                        window.open(HELP_GUIDE_URL, "_blank");
                      },
                    },
                  ]}
                />
              </Popover>

              <Button
                variant="tertiary"
                icon={guideExpanded ? ChevronUpIcon : ChevronDownIcon}
                accessibilityLabel={
                  guideExpanded ? t("onboarding_collapse") : t("onboarding_expand")
                }
                onClick={() => setGuideExpanded((expanded) => !expanded)}
              />
            </div>
          </div>

          {guideExpanded &&
            steps.map((step, index) => {
              const done = isComplete(step.id);
              const open = openStep === step.id;

              return (
                <div
                  key={step.id}
                  style={{
                    borderTop: "1px solid #e3e3e3",
                    background: done ? "#f7f7f7" : "#ffffff",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleStep(step.id)}
                    aria-expanded={open}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      width: "100%",
                      padding: "14px 24px",
                      background: "transparent",
                      border: 0,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <StepMarker complete={done} />
                    {/* Only the step being worked on is emphasised; the rest
                        stay at body weight, as in the reference design. */}
                    <Text
                      variant="bodyMd"
                      as="h3"
                      fontWeight={open ? "semibold" : "regular"}
                    >
                      {`${index + 1}. ${step.title}`}
                    </Text>
                  </button>

                  {open && <div style={{ padding: "0 24px 22px 62px" }}>{step.body}</div>}
                </div>
              );
            })}

          {guideExpanded && allDone && (
            <div
              style={{
                borderTop: "1px solid #e3e3e3",
                padding: "20px 24px",
                background: "#ffffff",
              }}
            >
              <Text variant="headingSm" as="h3">
                {t("onboarding_all_done_title")}
              </Text>
              <div style={{ marginTop: "4px", marginBottom: "14px" }}>
                <Text variant="bodyMd" as="p" tone="subdued">
                  {t("onboarding_all_done_description")}
                </Text>
              </div>
              <Button variant="primary" onClick={() => finishGuide(false)}>
                {t("onboarding_go_to_dashboard")}
              </Button>
            </div>
          )}
        </div>

        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <Button variant="plain" onClick={() => finishGuide(true)}>
            {t("onboarding_skip_and_go_to_dashboard")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
